import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Form, Input, message, Modal, Spin, Divider } from "antd";
import { SecurityScanOutlined, UserAddOutlined, LockOutlined } from "@ant-design/icons";
import request from "../../utils/request";
import authService from "../../services/auth";

const Profile = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [totpUri, setTotpUri] = useState(null);
  const [bindingDeadline, setBindingDeadline] = useState(null);
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [registerModalVisible, setRegisterModalVisible] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [registerTotpUri, setRegisterTotpUri] = useState(null);
  const [form] = Form.useForm();
  const [username, setUsername] = useState("");
  const [registerForm] = Form.useForm();
  const [passwordForm] = Form.useForm();

  useEffect(() => {
    checkTotpStatus();
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const payload = authService.parseJwt(token);
        if (payload) {
          setUsername(payload.username || "");
        }
      }
    } catch (error) {
      console.error("Failed to parse token for username:", error);
    }
  }, []);

  const checkTotpStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const payload = authService.parseJwt(token);
        if (payload) {
          setTotpEnabled(payload.totp_enabled);
          setBindingDeadline(payload.totp_binding_deadline);
        }
      }
    } catch (error) {
      console.error("Failed to parse token:", error);
    }
  };

  const getTotpUri = async () => {
    if (totpEnabled) {
      messageApi.warning("TOTP已绑定，无法重复绑定");
      return;
    }

    setLoading(true);
    try {
      const response = await request.get("/api/totp_uri");
      setTotpUri(response.totp_uri);
      setBindingDeadline(response.binding_deadline);
    } catch (error) {
      messageApi.error("获取TOTP二维码失败");
    } finally {
      setLoading(false);
    }
  };

  const bindTotp = async (values) => {
    setLoading(true);
    try {
      const response = await request.post("/api/bind_totp", {
        totp_code: values.totpCode,
      });

      localStorage.setItem("token", response.token);
      setTotpEnabled(true);
      messageApi.success("TOTP绑定成功！");
      form.resetFields();
      setTotpUri(null);
    } catch (error) {
      messageApi.error(error.error || "TOTP绑定失败");
    } finally {
      setLoading(false);
    }
  };

  const registerUser = async (values) => {
    setLoading(true);
    try {
      const result = await authService.register({
        username: values.username,
        password: values.password,
      });

      if (result.success) {
        messageApi.success("用户注册成功！");
        registerForm.resetFields();
        setRegisterModalVisible(false);
      } else {
        messageApi.error(result.error);
      }
    } catch (error) {
      messageApi.error("注册失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (values) => {
    setLoading(true);
    try {
      const result = await authService.changePassword(values.oldPassword, values.newPassword);
      
      if (result.success) {
        messageApi.success("密码修改成功！");
        passwordForm.resetFields();
        setPasswordModalVisible(false);
      } else {
        messageApi.error(result.error);
      }
    } catch (error) {
      messageApi.error("密码修改失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  };

  const getTimeRemaining = () => {
    if (!bindingDeadline) return null;

    const deadline = new Date(bindingDeadline);
    const now = new Date();
    const diff = deadline - now;

    if (diff <= 0) return "已过期";

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    return `${hours}小时${minutes}分钟`;
  };

  return (
    <div style={{ padding: "24px" }}>
      {contextHolder}
      <Card title="个人信息" style={{ marginBottom: "24px" }}>
        <p>用户名: {username}</p>
      </Card>

      <Card
        title="双因素认证 (TOTP)"
        icon={<SecurityScanOutlined />}
        style={{ marginBottom: "24px" }}
      >
        {totpEnabled
          ? (
            <Alert
              message="TOTP已绑定"
              description="您的账户已成功绑定双因素认证，安全性更高。"
              type="success"
              showIcon
            />
          )
          : (
            <div>
              {!totpEnabled && bindingDeadline && (
                <Alert
                  message="需要绑定TOTP"
                  description={`请在 ${getTimeRemaining()} 内绑定TOTP，否则账户将被锁定。`}
                  type="warning"
                  showIcon
                  style={{ marginBottom: "16px" }}
                />
              )}

              {!totpUri
                ? (
                  <Button
                    type="primary"
                    onClick={getTotpUri}
                    loading={loading}
                    icon={<SecurityScanOutlined />}
                  >
                    获取TOTP二维码
                  </Button>
                )
                : (
                  <div>
                    <Alert
                      message="绑定TOTP"
                      description="请使用认证器应用（如 Google Authenticator）完成绑定。"
                      type="info"
                      showIcon
                      style={{ marginBottom: "16px" }}
                    />

                    <div
                      style={{
                        textAlign: "center",
                        marginBottom: "24px",
                      }}
                    >
                      <p>{totpUri}</p>
                    </div>

                    <Form
                      form={form}
                      onFinish={bindTotp}
                      layout="inline"
                    >
                      <Form.Item
                        name="totpCode"
                        rules={[
                          {
                            required: true,
                            message: "请输入验证码",
                          },
                          {
                            len: 6,
                            message:
                              "验证码必须是6位数字",
                          },
                        ]}
                      >
                        <Input
                          placeholder="输入6位验证码"
                          maxLength={6}
                        />
                      </Form.Item>
                      <Form.Item>
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={loading}
                        >
                          绑定TOTP
                        </Button>
                      </Form.Item>
                    </Form>
                  </div>
                )}
            </div>
          )}
      </Card>

      <Card title="安全设置" style={{ marginBottom: "24px" }}>
        <Button
          type="primary"
          icon={<LockOutlined />}
          onClick={() => setPasswordModalVisible(true)}
          style={{ marginBottom: "16px" }}
        >
          修改密码
        </Button>

        <Modal
          title="修改密码"
          open={passwordModalVisible}
          onCancel={() => {
            setPasswordModalVisible(false);
            passwordForm.resetFields();
          }}
          footer={null}
          width={400}
        >
          <Form
            form={passwordForm}
            onFinish={changePassword}
            layout="vertical"
          >
            <Form.Item
              name="oldPassword"
              label="当前密码"
              rules={[
                { required: true, message: "请输入当前密码!" }
              ]}
            >
              <Input.Password placeholder="请输入当前密码" />
            </Form.Item>

            <Form.Item
              name="newPassword"
              label="新密码"
              rules={[
                { required: true, message: "请输入新密码!" },
                { min: 8, message: "密码至少8个字符!" }
              ]}
            >
              <Input.Password placeholder="请输入新密码" />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label="确认新密码"
              dependencies={["newPassword"]}
              rules={[
                { required: true, message: "请确认新密码!" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("newPassword") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("两次输入的密码不一致!"));
                  },
                }),
              ]}
            >
              <Input.Password placeholder="请再次输入新密码" />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
              >
                修改密码
              </Button>
            </Form.Item>
          </Form>
        </Modal>

        <Divider />

        {totpEnabled && (
          <div>
            <Alert
              message="TOTP已绑定"
              description="您的账户已成功绑定双因素认证，安全性更高。"
              type="success"
              showIcon
              style={{ marginBottom: "16px" }}
            />

            <Button
              type="primary"
              icon={<UserAddOutlined />}
              onClick={() => setRegisterModalVisible(true)}
            >
              注册新用户
            </Button>

            <Modal
              title="注册新用户"
              open={registerModalVisible}
              onCancel={() => {
                setRegisterModalVisible(false);
                setRegisterTotpUri(null);
                registerForm.resetFields();
              }}
              footer={null}
              width={500}
            >
              <Form
                form={registerForm}
                onFinish={registerUser}
                layout="vertical"
              >
                <Form.Item
                  name="username"
                  label="用户名"
                  rules={[
                    {
                      required: true,
                      message: "请输入用户名!",
                    },
                    { min: 3, message: "用户名至少3个字符!" },
                  ]}
                >
                  <Input placeholder="用户名" />
                </Form.Item>

                <Form.Item
                  name="password"
                  label="密码"
                  rules={[
                    { required: true, message: "请输入密码!" },
                    { min: 8, message: "密码至少8个字符!" },
                  ]}
                >
                  <Input.Password placeholder="密码" />
                </Form.Item>

                <Form.Item
                  name="confirmPassword"
                  label="确认密码"
                  dependencies={["password"]}
                  rules={[
                    { required: true, message: "请确认密码!" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (
                          !value ||
                          getFieldValue("password") ===
                          value
                        ) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error(
                            "两次输入的密码不一致!",
                          ),
                        );
                      },
                    }),
                  ]}
                >
                  <Input.Password placeholder="确认密码" />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    block
                  >
                    注册用户
                  </Button>
                </Form.Item>
              </Form>
            </Modal>
          </div>
        )}
      </Card>
    </div>
  );
};

export default Profile;
